// Scanner-Eingabe (design-system §4.27): Ein Handscanner tippt wie eine sehr schnelle Tastatur und schließt mit
// Enter ab. `useScanInput` erkennt solche Folgen am Dokument – unabhängig davon, welches Element den Fokus hat.
import { useEffect, useRef } from 'react'
import type { IsoDate } from '@shared/time'

export type BarcodeFormat = 'ean_13' | 'ean_8' | 'upc_a' | 'code_128' | 'itf' | 'qr_code' | 'data_matrix'

export interface Gs1Data {
  gtin?: string
  lot?: string
  expiry?: IsoDate
  serial?: string
}

export interface ScanResult {
  code: string
  format: BarcodeFormat | 'unknown'
  /** wedge = Handscanner als Tastatur */
  source: 'camera' | 'wedge' | 'manual'
  /** aus GS1-128 / DataMatrix */
  gs1?: Gs1Data
  at: number
}

const GS = '\u001d'

/** Liest die gängigen GS1-Datenbezeichner 01 (GTIN), 10 (Charge), 17 (MHD), 21 (Seriennummer). */
export function parseGs1(raw: string): Gs1Data | undefined {
  let text = raw.replace(/^\]C1|^\]d2|^\]Q3/, '')
  const result: Gs1Data = {}
  if (text.includes('(')) {
    for (const match of text.matchAll(/\((\d{2})\)([^(]+)/g))
      assign(result, match[1] as string, (match[2] as string).trim())
    return Object.keys(result).length > 0 ? result : undefined
  }
  if (!/^(01|10|17|21)/.test(text) || text.length < 16) return undefined
  while (text.length > 0) {
    if (text.startsWith(GS)) {
      text = text.slice(1)
      continue
    }
    const ai = text.slice(0, 2)
    if (ai === '01') {
      assign(result, ai, text.slice(2, 16))
      text = text.slice(16)
    } else if (ai === '17') {
      assign(result, ai, text.slice(2, 8))
      text = text.slice(8)
    } else if (ai === '10' || ai === '21') {
      const end = text.indexOf(GS)
      const value = end === -1 ? text.slice(2) : text.slice(2, end)
      assign(result, ai, value)
      text = end === -1 ? '' : text.slice(end + 1)
    } else {
      break
    }
  }
  return Object.keys(result).length > 0 ? result : undefined
}

function assign(target: Gs1Data, ai: string, value: string): void {
  if (ai === '01') target.gtin = value
  else if (ai === '10') target.lot = value
  else if (ai === '21') target.serial = value
  else if (ai === '17' && /^\d{6}$/.test(value)) {
    const yy = Number(value.slice(0, 2))
    const month = value.slice(2, 4)
    let day = value.slice(4, 6)
    const year = 2000 + yy
    // Tag 00 = nur Monat bekannt → letzter Tag des Monats
    if (day === '00') day = String(new Date(Date.UTC(year, Number(month), 0)).getUTCDate()).padStart(2, '0')
    target.expiry = `${year}-${month}-${day}`
  }
}

/** Format aus der Ziffernfolge schätzen (Handscanner melden keins). */
export function guessFormat(code: string): ScanResult['format'] {
  if (/^\d{13}$/.test(code)) return 'ean_13'
  if (/^\d{8}$/.test(code)) return 'ean_8'
  if (/^\d{12}$/.test(code)) return 'upc_a'
  if (/^\d{14}$/.test(code)) return 'itf'
  return 'unknown'
}

export function toScanResult(code: string, source: ScanResult['source'], format?: ScanResult['format']): ScanResult {
  const gs1 = parseGs1(code)
  return { code, format: format ?? guessFormat(code), source, at: Date.now(), ...(gs1 ? { gs1 } : {}) }
}

export interface ScanDetectorOptions {
  /** Standard 6 */
  minLength?: number
  /** Standard 35 */
  maxKeyIntervalMs?: number
  /** Standard 'Enter' */
  terminator?: 'Enter' | 'Tab'
}

/**
 * Erkennt Scanner-Folgen aus einzelnen Tastendrücken. `push` liefert den Code, sobald eine Folge mit dem
 * Abschlusszeichen endet und schnell genug getippt wurde – sonst null. Reine Logik, ohne DOM.
 */
export function createScanDetector(options: ScanDetectorOptions = {}) {
  const { minLength = 6, maxKeyIntervalMs = 35, terminator = 'Enter' } = options
  let buffer = ''
  let last = 0
  return {
    push(key: string, at: number): string | null {
      if (key === terminator) {
        const code = buffer
        const fast = at - last <= maxKeyIntervalMs * 3
        buffer = ''
        return code.length >= minLength && fast ? code : null
      }
      if (key.length !== 1) return null
      if (buffer !== '' && at - last > maxKeyIntervalMs) buffer = ''
      buffer += key
      last = at
      return null
    },
    /** Zahl der Zeichen, die gerade als mögliche Scanner-Folge gelten */
    get pending(): number {
      return buffer.length
    },
    reset(): void {
      buffer = ''
    },
  }
}

export interface UseScanInputOptions extends ScanDetectorOptions {
  enabled?: boolean
  /** Standard false: in Textfeldern nur, wenn das Feld `data-scan-target` trägt (Input mit onScan) */
  captureInInputs?: boolean
}

const isTextField = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT')

/** Handscanner: erkennt schnelle Tastenfolgen mit Abschlusszeichen – unabhängig vom Fokus. */
export function useScanInput(handler: (result: ScanResult) => void, options: UseScanInputOptions = {}): void {
  const { enabled = true, captureInInputs = false, minLength, maxKeyIntervalMs, terminator } = options
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    if (!enabled) return
    const detector = createScanDetector({ minLength, maxKeyIntervalMs, terminator })
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (isTextField(event.target)) {
        // Felder mit eigenem onScan behandeln den Scan selbst (Input setzt data-scan-target)
        if ((event.target as HTMLElement).dataset.scanTarget !== undefined) return
        if (!captureInInputs) return
      }
      const code = detector.push(event.key, event.timeStamp || Date.now())
      if (code !== null) {
        event.preventDefault()
        handlerRef.current(toScanResult(code, 'wedge'))
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [enabled, captureInInputs, minLength, maxKeyIntervalMs, terminator])
}
